import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const inlineButtonVariants = cva(
  [
    "inline-flex items-center justify-center",
    // 34px container + the 8px padding of the row it sits in = a 50px touch
    // target, past the WCAG 2.5.8 AAA minimum. The glyph is 19px, NOT the 24px
    // the component's Figma description claims: the description is stale, and
    // every real instance measures 34 around an 18.89 icon.
    "size-8.5 shrink-0 rounded-lg",
    "border-0 outline-none",
    "transition-colors duration-150 ease-out",
    "cursor-pointer",
    "[&>svg]:size-4.75",
  ],
  {
    variants: {
      state: {
        default: "bg-transparent text-ds-slate-700",
        resting: "bg-ds-mint-800 text-ds-on-primary",
        // A shade past `resting`: the two sit side by side in the composer
        // (voice + send), so the primary action has to read as the darker one.
        active: "bg-ds-mint-900 text-ds-on-primary",
        pending: "bg-ds-slate-100 text-ds-black",
        // A turn in flight (ChatInput "Streaming"): the dark round stop.
        stop: "rounded-full bg-ds-slate-950 text-ds-white",
        disabled: "bg-ds-slate-100 text-ds-slate-400 cursor-not-allowed",
        // The two halves of the dictation prompt (discard / keep), which the
        // ChatInput maquette tints outside the neutral-to-mint ramp so the
        // destructive half is unmistakable.
        danger: "bg-ds-strawberry-100 text-ds-strawberry-500",
        confirm: "bg-ds-moss-200 text-ds-mint-900",
      },
    },
    defaultVariants: {
      state: "default",
    },
  }
);

type InlineButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "disabled"
> &
  VariantProps<typeof inlineButtonVariants>;

const InlineButton = forwardRef<HTMLButtonElement, InlineButtonProps>(
  function InlineButton({ className, state, ...props }, ref) {
    return (
      <button
        ref={ref}
        disabled={state === "disabled"}
        className={cn(inlineButtonVariants({ state }), className)}
        {...props}
      />
    );
  }
);

export { InlineButton, inlineButtonVariants, type InlineButtonProps };
